import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Tooltip, Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";
// REDUX
import { useSelector } from "react-redux";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
// CUSTOM HOOKS
import { useLastLiftMessage, useDebounceMap } from "../socketMessageState/BankMessagesContext";
import useDirectionDebounce from "../iconDefinition/useDirectionDebounce";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
import MonitoringIconsMovingWalks from "../monitoringIcons/MonitoringIconsMovingWalks";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/EdgeManagement.interface";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UTILS
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import {
  usageToDoorIconFiltered,
  usageHasDoorData,
  usageHasDirectionData,
  hasMixedDoorConfig,
} from "../../../utils/bitmaskToIcon";
import { stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

export default function ControlPanelLiftHeader({ server }: { server: ServerInterface }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  // Service mode display is handled via getServiceModeDisplay utility

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [currentPositionNameFloor, setCurrentPositionNameFloor] = useState<string>("");
  const [nextStopNameFloor, setNextStopNameFloor] = useState<string>("");
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { liftSelected, bankSelected, doorUnknownAsOpening } = useSelector(
    (state: RootState) => state.monitoring,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(
    () => (liftSelected.id ? stringToUuidT(String(liftSelected.id)) : EMPTY_UUID),
    [liftSelected.id],
  );
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;
  const debounceMap = useDebounceMap();

  const bankHasMixedDoors = useMemo(
    () => hasMixedDoorConfig(liftSelected.liftFloors),
    [liftSelected.liftFloors],
  );

  // Get current floor configuration based on elevator position
  const currentFloor = useMemo(
    () =>
      (bankSelected.bankFloors ?? []).find((f) => f.floorIndex === socketMessageLift.position) ??
      null,
    [bankSelected.bankFloors, socketMessageLift.position],
  );
  // These defaults fire on a lookup MISS (currentFloor null), not an absent field on a
  // found floor — BLANK_BANK carries no bankFloors at all, so this fires routinely
  // whenever the drawer is closed, and a reported position outside the configured range
  // matches nothing either way. Not knowing which floor the car is at must not invent an
  // opening: bank.fbs declares BankFloor.front_door = true, rear_door = false, and every
  // hydrated reader (normalizeFloors in MonitoringBank/TerminalPage, bankFloorOpenings,
  // requestSerializers) already resolves an absent rear door to false — match that.
  const hasFrontDoor = useMemo(() => currentFloor?.frontDoor ?? true, [currentFloor]);
  const hasRearDoor = useMemo(() => currentFloor?.rearDoor ?? false, [currentFloor]);

  const doorIcon = useMemo(
    () =>
      usageToDoorIconFiltered(
        socketMessageLift.usage,
        hasFrontDoor,
        hasRearDoor,
        doorUnknownAsOpening,
        bankHasMixedDoors,
      ),
    [socketMessageLift.usage, doorUnknownAsOpening, hasFrontDoor, hasRearDoor, bankHasMixedDoors],
  );
  const { directionIcon, lastDirectionIcon } = useDirectionDebounce(
    socketMessageLift.usage,
    debounceMap,
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getFloorName = (socketMessage_: SocketMessageInterface) => {
    (bankSelected.bankFloors ?? []).map((floor: BankFloorInterface) => {
      if (socketMessage_.position === floor.floorIndex) {
        setCurrentPositionNameFloor(floor.name);
      }
      if (
        socketMessage_.carCalls &&
        (socketMessage_.carCalls.front[0] || socketMessage_.carCalls.rear[0]) &&
        (socketMessage_.carCalls.front[0] === floor.floorIndex ||
          socketMessage_.carCalls.rear[0] === floor.floorIndex)
      ) {
        setNextStopNameFloor(floor.name);
      }
      return true;
    });
  };

  const getCarCallCircle = (carCall: number, index: number, type: "up" | "down" | "normal") => {
    let floorName = `Error, floor ${carCall} not found or invalid configuration`;
    let floorError = true;
    (bankSelected.bankFloors ?? []).map((floor: BankFloorInterface) => {
      if (carCall === floor.floorIndex) {
        floorName = floor.name ?? `F${carCall}`;
        floorError = false;
      }
      return true;
    });
    return (
      <Tooltip title={floorName}>
        <div
          key={index}
          className={`monitoringView__headerCarCall ${
            floorError
              ? "monitoringView__headerCarCall__error"
              : `monitoringView__headerCarCall__${theme} ${
                  type === "up" ? "monitoringView__headerCarCall__up" : ""
                } ${type === "down" ? "monitoringView__headerCarCall__down" : ""}`
          }`}
        >
          {floorName.substring(0, 2).toUpperCase()}
        </div>
      </Tooltip>
    );
  };

  // ************************************************* */
  // USE EFFECT ************************************** */

  useEffect(() => {
    if (liftSelected.type === LiftType.ELEVATOR) {
      getFloorName(socketMessageLift);
    }
  }, [socketMessageLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className={`monitoringView__controlPanel__${theme} ${
        hasFaults
          ? "monitoringView__dangerElevator monitoringView__dangerElevator__top monitoringView__dangerElevator__bottom"
          : ""
      }`}
    >
      <div
        className={`monitoringView__controlPanelDetails monitoringView__controlPanelDetails__${theme}`}
      >
        {/** ICON AND TAGS SERVICE / FAULTS */}
        <div className="generalStyles__flexCenter monitoring__100pxBox2">
          <div>
            <div style={{ textAlign: "center" }} className="mt-1">
              {liftSelected.type === LiftType.ELEVATOR &&
                (() => {
                  const hasDoorData = usageHasDoorData(socketMessageLift.usage);
                  const hasDirection = usageHasDirectionData(socketMessageLift.usage);
                  const posReceived = socketMessageLift.position !== null;
                  const isDegraded = !posReceived || !hasDoorData || !hasDirection;

                  return (
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: isDegraded && !hasDoorData && !hasDirection ? 0.35 : 1,
                        }}
                      >
                        {hasDoorData && doorIcon && (
                          <IconDefinition
                            isMap={false}
                            expanded={false}
                            liftType={LiftType[LiftType.ELEVATOR]}
                            icon={doorIcon}
                            iconSize={50}
                          />
                        )}
                        {hasDirection && (directionIcon || lastDirectionIcon) && (
                          <span
                            style={{
                              opacity: !directionIcon && lastDirectionIcon ? 0.3 : 1,
                            }}
                          >
                            <IconDefinition
                              isMap={false}
                              expanded={false}
                              liftType={LiftType[LiftType.ELEVATOR]}
                              icon={(directionIcon || lastDirectionIcon) as string}
                              iconSize={50}
                            />
                          </span>
                        )}
                        {!hasDoorData && !hasDirection && (
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "12px", opacity: 0.5 }}
                          >
                            Awaiting data
                          </Typography.Text>
                        )}
                      </div>
                      {isDegraded && (
                        <Tooltip
                          placement="bottom"
                          title={`Awaiting: ${[
                            !posReceived ? "position" : "",
                            !hasDoorData ? "door status" : "",
                            !hasDirection ? "direction" : "",
                          ]
                            .filter(Boolean)
                            .join(", ")}`}
                        >
                          <WarningOutlined
                            style={{
                              color: "#faad14",
                              fontSize: "14px",
                              position: "absolute",
                              top: 0,
                              right: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  );
                })()}
              {liftSelected.type === LiftType.ESCALATOR && (
                <IconDefinitionEscalatorDefault iconSize={40} mapViewClass="" lift={liftSelected} />
              )}
              {liftSelected.type === LiftType.MOVINGWALK && (
                <MonitoringIconsMovingWalks
                  direction={socketMessageLift.direction}
                  iconSize={60}
                  mapViewClass=""
                />
              )}
            </div>
            <div className="generalStyles__flexCenter mb-3">
              <div style={{ textAlign: "center", minHeight: "25px", width: "120px" }}>
                {hasFaults && (
                  <Tag
                    color="red"
                    style={{
                      width: "120px",
                      textAlign: "center",
                      marginTop: "10px",
                      marginLeft: "10px",
                    }}
                  >
                    FAULT
                  </Tag>
                )}
                {socketMessageLift.serviceMode &&
                  (() => {
                    const display = getServiceModeDisplay(
                      socketMessageLift.serviceMode.messageType,
                      socketMessageLift.serviceMode.message?.message?.message ?? null,
                    );
                    if (!display) return null;
                    return (
                      <Tooltip placement="top" title={display.displayName}>
                        <Tag
                          style={{
                            marginTop: "5px",
                          }}
                          color={display.color}
                        >
                          {display.abbreviation.substring(0, 6)}
                        </Tag>
                      </Tooltip>
                    );
                  })()}
              </div>
            </div>
          </div>
        </div>
        {/** CAR NAME, SOCKET MESSAGE AND CALLS */}
        <div
          className="generalStyles__mlFix25 generalStyles__width100 generalStyles__flex"
          style={{ alignItems: "center" }}
        >
          <div>
            <div style={{ marginTop: "-10px", height: "35px" }}>
              <span className="monitoring__modalSettingsHeader__liftName">{liftSelected.name}</span>
              <span className="generalStyles__mrFix generalStyles__mlFix">/</span>
              <span>{bankSelected.name}</span>
              <span className="generalStyles__mrFix generalStyles__mlFix">/</span>
              <span>{server.fbEnabled ? "FB" : "CMD"}</span>
            </div>
            {/** CAR CALLS */}
            <div
              className="generalStyles__flex"
              style={{ maxWidth: "315px", overflowX: "auto", padding: "10px" }}
            >
              <div className="generalStyles__flex">
                {socketMessageLift.carCalls.front.map((carCall: number, index: number) => (
                  <>{getCarCallCircle(carCall, index, "normal")}</>
                ))}
                {socketMessageLift.carCalls.rear.map((carCall: number, index: number) => (
                  <>{getCarCallCircle(carCall, index, "normal")}</>
                ))}
                {socketMessageLift.hallCalls.upAssigned.front.map(
                  (carCall: number, index: number) => (
                    <>{getCarCallCircle(carCall, index, "up")}</>
                  ),
                )}
                {socketMessageLift.hallCalls.upAssigned.rear.map(
                  (carCall: number, index: number) => (
                    <>{getCarCallCircle(carCall, index, "up")}</>
                  ),
                )}
                {socketMessageLift.hallCalls.downAssigned.front.map(
                  (carCall: number, index: number) => (
                    <>{getCarCallCircle(carCall, index, "down")}</>
                  ),
                )}
                {socketMessageLift.hallCalls.downAssigned.rear.map(
                  (carCall: number, index: number) => (
                    <>{getCarCallCircle(carCall, index, "down")}</>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
        {/** ELEVATOR DIRECTION */}
        <div className="generalStyles__flex">
          <div style={{ alignItems: "center", marginTop: "-10px" }} className="generalStyles__flex">
            <div>{currentPositionNameFloor}</div>
            {(socketMessageLift.carCalls.front.length > 0 ||
              socketMessageLift.carCalls.rear.length > 0) && (
              <>
                <div>
                  <FontAwesomeIcon
                    icon={faCaretRight}
                    className="monitoring__modalSettingsHeader__icon"
                  />
                </div>
                <div>{nextStopNameFloor}</div>
              </>
            )}
          </div>
        </div>
      </div>{" "}
    </div>
  );
}
